'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();

import { useEffect, useState } from 'react';
import { Card, CardBody, CardTitle, Row, Col, Form, Label, Input, Alert, FormGroup } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import usePhoneInputLabelLanguage from '../hooks/usePhoneInputLabelLanguage';
import Cta from './CtaBtn';
import { uploadContactForm } from '../api/contact';
import validateMail from '../utils/validateEmailAddress';
import { isValidCompanyName, isValidCompanyNumber, isValidPersonName, NAME_MAX_LENGTH } from '../utils/validateName';
import { ApiLocation, getLocationsForAutocomplete } from '../api/locations';
import { plausibleEvent } from '../utils/plausibleEvent';
import { logSentryError } from '../utils/logSentry';

import 'react-phone-number-input/style.css';
import { Dictionaries, Locale } from '../i18n/i18n-config';
import { getI18nUtils } from '../i18n/i18n-utils';

const defaultInputs = {
  companyName: {
    type: 'text',
    value: '',
    touched: false,
    valid: false,
    minLength: 2,
    maxLength: NAME_MAX_LENGTH,
    intlId: 'contact-form-companyName',
    order: 1,
    required: true,
  },
  companyNumber: {
    type: 'text',
    value: '',
    touched: false,
    valid: true,
    intlId: 'contact-form-companyNumber',
    order: 2,
    required: false,
  },
  firstName: {
    type: 'text',
    value: '',
    touched: false,
    valid: false,
    minLength: 2,
    maxLength: NAME_MAX_LENGTH,
    intlId: 'contact-form-firstName',
    order: 3,
    required: true,
  },
  lastName: {
    type: 'text',
    value: '',
    touched: false,
    valid: false,
    minLength: 2,
    maxLength: NAME_MAX_LENGTH,
    intlId: 'contact-form-lastName',
    order: 4,
    required: true,
  },
  telephone: {
    type: 'tel',
    value: '',
    touched: false,
    valid: false,
    intlId: 'contact-form-phone',
    order: 5,
    required: true,
  },
  email: {
    type: 'email',
    value: '',
    touched: false,
    valid: false,
    intlId: 'contact-form-email',
    order: 6,
    required: true,
  },
  postalCode: {
    type: 'text',
    intlId: 'contact-form-postalCode',
    order: 7,
    required: false,
    value: 'postalCode',
    valid: true,
  },
  message: {
    type: 'textarea',
    value: '',
    touched: false,
    valid: false,
    minLength: 10,
    intlId: 'contact-form-message',
    order: 8,
    required: true,
    placeholder: 'employers-contact-message-label',
  },
};
type Props = {
  dictionary: Partial<Dictionaries>;
  locale: Locale;
};

function ContactForm({ dictionary, locale }: Props): JSX.Element {
  const [inputs, setInputs] = useState(defaultInputs);
  const [location, setLocation] = useState<ApiLocation | null>(null);
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const phoneLabelLanguage = usePhoneInputLabelLanguage();
  const { formatMessage } = getI18nUtils(dictionary);
  const app = process.env.NEXT_PUBLIC_APP;

  useEffect(() => {
    if (completed) {
      setTimeout(() => {
        setCompleted(false);
        setInputs(defaultInputs);
        setLocation(null);
        setTermsAccepted(false);
        setFormIsValid(false);
      }, 3000);
    }
  }, [completed]);

  // validate a specific input (zelfde regels als POST /business/contact)
  function isInputValid(input, inputType: string) {
    const { value, type, minLength, maxLength, required } = input;
    if (value === '' && required) return false;

    if (minLength && value.trim().length < minLength) return false;

    if (maxLength && value.length > maxLength) return false;

    if (type === 'email' && !validateMail(value)) return false;

    if (type === 'tel' && !isValidPhoneNumber(value)) return false;

    if (inputType === 'companyName') return isValidCompanyName(value, minLength);
    if (inputType === 'companyNumber') return isValidCompanyNumber(value);
    if (inputType === 'firstName' || inputType === 'lastName') return isValidPersonName(value, minLength);

    return true;
  }

  const checkFormValidity = () => {
    const validForm: boolean = Object.keys(inputs).every((input) => inputs[input].valid) && termsAccepted && Boolean(location);

    setFormIsValid(validForm);
    return validForm;
  };

  async function onSubmitHandler(e) {
    e.preventDefault();
    setSubmitting(true);
    setErrorMessage(null);
    if (!checkFormValidity) return;
    const { companyName, companyNumber, firstName, lastName, telephone, message, email } = inputs;
    const body = {
      companyName: companyName.value,
      companyNumber: companyNumber.value === '' ? undefined : companyNumber.value,
      firstName: firstName.value,
      lastName: lastName.value,
      telephone: telephone.value,
      email: email.value,
      postalCode: location?.postalCode,
      message: message.value,
      language: locale,
      app: app || 'synergiejobs.be',
      location: { lat: location?.lat, lng: location?.lng },
    };
    try {
      await uploadContactForm(body);
      plausibleEvent('businessContactFormSubmission');
      setCompleted(true);
    } catch (error) {
      logSentryError(error);
      setErrorMessage(formatMessage({ id: 'internal-server-error' }));
    }
    setSubmitting(false);
  }

  // validate the form when an input changes
  useEffect(() => {
    checkFormValidity();
  }, [inputs, termsAccepted, location]);

  function inputChangedHandler(e, inputType) {
    setErrorMessage(null);
    const inputCp = { ...inputs[inputType] };
    inputCp.touched = true;
    inputCp.value = inputType === 'telephone' ? e : e.target.value;
    inputCp.valid = isInputValid(inputCp, inputType);
    setInputs({ ...inputs, [inputType]: inputCp });
  }

  function changeLocation(loc) {
    return setLocation(loc);
  }

  const customStyles = {
    control: (provided, state) => {
      let boxShadow = '';
      if (location && state.isFocused) boxShadow = '0 0 0 0.2rem rgba(40, 167, 69, 0.25)';
      if (!location && state.isFocused) boxShadow = '0 0 0 0.2rem rgba(226, 0, 26, 0.25)';

      let border = '';
      if (location) border = '1px solid #28a745';
      if (!location && state.isFocused) border = '1px solid red';

      return {
        ...provided,
        border,
        boxShadow,
      };
    },
  };

  const sortedFields = Object.keys(inputs).sort((a, b) => (inputs[a].order > inputs[b].order ? 1 : -1));
  return (
    <Card id="contactForm" className={`backgroundMargin${submitting ? ' submitting' : ''}`}>
      <CardBody>
        {!completed ? (
          <>
            <CardTitle tag="h2" className="mb-4">
              {formatMessage({ id: 'contact-form-contact-us' })}
            </CardTitle>
            <Form onSubmit={(e) => onSubmitHandler(e)}>
              <fieldset disabled={submitting} aria-busy={submitting}>
                <Row>
                  {sortedFields.map((inputType, index) => {
                    const { type, value, touched, valid, minLength, maxLength, intlId, required, placeholder } =
                      inputs[inputType];
                    return (
                      <Col xs={12} md={index === sortedFields.length - 1 ? 12 : 6} key={inputType}>
                        {(inputType === 'postalCode' && (
                          <>
                            <Label className="required text-muted">{formatMessage({ id: 'contact-form-postalCode' })}</Label>
                            <AsyncSelect
                              value={location}
                              loadOptions={getLocationsForAutocomplete}
                              getOptionLabel={(option) => `${option.postalCode} ${option.city}`}
                              getOptionValue={(option) => `${option.postalCode} ${option.city}`}
                              onChange={(loc) => changeLocation(loc)}
                              id="location"
                              defaultOptions
                              placeholder={formatMessage({ id: 'contact-form-select-postalCode' })}
                              className="react-select mb-3"
                              classNamePrefix="react-select"
                              styles={customStyles}
                              onBlur={checkFormValidity}
                            />
                          </>
                        )) || (
                          <>
                            <Label className={`text-muted${required ? ' required' : ''}`} for={inputType}>
                              {formatMessage({ id: intlId })}
                            </Label>
                            {inputType === 'telephone' ? (
                              <PhoneInput
                                className={`${touched && valid ? 'is-valid' : ''} ${touched && !valid ? 'is-invalid' : ''}`}
                                id={inputType}
                                name={inputType}
                                defaultCountry="BE"
                                required={required}
                                value={value}
                                labels={phoneLabelLanguage}
                                international
                                countryCallingCodeEditable={false}
                                // value is stored directly in 'e' so 'e' can be undefined
                                onChange={(e) => e && inputChangedHandler(e, inputType)}
                                onBlur={() => checkFormValidity()}
                              />
                            ) : (
                              <Input
                                name={inputType}
                                id={inputType}
                                value={value}
                                minLength={minLength}
                                maxLength={maxLength}
                                required={required}
                                type={type}
                                valid={touched && valid}
                                invalid={touched && !valid}
                                className="mb-3"
                                onChange={(e) => inputChangedHandler(e, inputType)}
                                onBlur={checkFormValidity}
                                placeholder={placeholder ? formatMessage({ id: placeholder }) : ''}
                              />
                            )}
                          </>
                        )}
                      </Col>
                    );
                  })}
                </Row>
                <Row className="mt-3">
                  <Col xs={12}>
                    <FormGroup check inline>
                      <Input
                        type="checkbox"
                        id="terms"
                        name="terms"
                        onChange={() => {
                          setTermsAccepted(!termsAccepted);
                        }}
                        checked={termsAccepted}
                      />
                      <Label for="terms" check>
                        {formatMessage(
                          { id: 'accept-privacy-terms' },
                          {
                            privacyLink: (
                              <a href={`/${locale}/privacy`} target="_blank" rel="noopener noreferrer">
                                {formatMessage({ id: 'privacy_policy' })}
                              </a>
                            ),
                            termsLink: (
                              <a href={`/${locale}/termsandconditions`} target="_blank" rel="noopener noreferrer">
                                {formatMessage({ id: 'termsandconditions' })}
                              </a>
                            ),
                          }
                        )}
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
                {errorMessage && (
                  <Alert color="danger" className="py-2">
                    {errorMessage}
                  </Alert>
                )}
                <Cta className="cta red noArrow" type="submit" disabled={!formIsValid}>
                  {formatMessage({ id: 'contact-form-submit' })}
                </Cta>
              </fieldset>
            </Form>
          </>
        ) : (
          <Alert color="success">{formatMessage({ id: 'contact-form-thanks' })}</Alert>
        )}
      </CardBody>
    </Card>
  );
}

export default ContactForm;
